import React, { useMemo } from 'react';
import logoImage from '../assets/images/logo.jpeg';
import { NAV_LINKS, ANALYTICS_NAV_LINKS } from './navConfig';

const Header = ({
  currentUser,
  roles,
  onProfileClick,
  onToggleSidebar,
  activeSection = 'dashboard',
  isSidebarCollapsed = false
}) => {
  const getStoredIdentityValue = (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  const displayName =
    currentUser?.firstName ||
    currentUser?.given_name ||
    getStoredIdentityValue('firstName') ||
    currentUser?.username ||
    getStoredIdentityValue('username') ||
    'User';
  const username = currentUser?.username || getStoredIdentityValue('username') || 'User';

  const userRoles = Array.isArray(roles) ? roles : [];

  // Role hierarchy mapping (updated to include system administrator as highest priority)
  const roleHierarchy = {
    'systemadministrator': { priority: 1, label: 'Admin', color: '#ef4444' },
    'organizationadministrator': { priority: 2, label: 'Org Admin', color: '#f97316' },
    'sitecollectionowner': { priority: 3, label: 'Site Owner', color: '#f97316' },
    'contentcreator': { priority: 4, label: 'Content Creator', color: '#3b82f6' },
    'regularuser': { priority: 5, label: 'User', color: '#6b7280' }
  };

  // Get highest priority role
  const getHighestRole = (userRoles) => {
    if (!userRoles || userRoles.length === 0) {
      return roleHierarchy['regularuser'];
    }

    let highestRole = null;
    let highestPriority = Infinity;

    userRoles.forEach(role => {
      const roleLower = role.toLowerCase().trim().replace(/[\s_-]/g, '');
      const roleConfig = Object.keys(roleHierarchy).find(key =>
        roleLower === key.replace(/[\s_-]/g, '')
      );

      if (roleConfig && roleHierarchy[roleConfig].priority < highestPriority) {
        highestPriority = roleHierarchy[roleConfig].priority;
        highestRole = roleHierarchy[roleConfig];
      }
    });

    return highestRole || roleHierarchy['regularuser'];
  };

  const displayRole = getHighestRole(userRoles);

  const activeLabel = useMemo(() => {
    if (!activeSection) return 'Dashboard';
    if (NAV_LINKS[activeSection]) return NAV_LINKS[activeSection].label;
    const analyticsItem = ANALYTICS_NAV_LINKS.find(item =>
      item.id === activeSection || (item.aliases && item.aliases.includes(activeSection))
    );
    if (analyticsItem) return analyticsItem.label;
    return activeSection.charAt(0).toUpperCase() + activeSection.slice(1);
  }, [activeSection]);

  return (
    <>
      <style>{`
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 28px;
          height: 64px;
          min-height: 64px;
          background: linear-gradient(90deg, #1e1b4b 0%, #2e2a72 100%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.12);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
          position: relative;
          z-index: 50;
          box-sizing: border-box;
          width: 100%;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }


        .header-brand {
          display: flex;
          align-items: center;
          gap: 10px;
          user-select: none;
        }

        .header-logo {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          object-fit: cover;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
        }

        .header-brand-title {
          font-size: 19px;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: 1.2px;
          margin: 0;
        }

        .header-divider {
          width: 1px;
          height: 22px;
          background: rgba(255, 255, 255, 0.2);
        }

        .header-active-badge {
          display: inline-flex;
          align-items: center;
          font-size: 13px;
          font-weight: 600;
          color: #e0e7ff;
          background: rgba(255, 255, 255, 0.1);
          padding: 4px 12px;
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.15);
          letter-spacing: 0.2px;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .header-greeting {
          color: #c7d2fe;
          font-size: 14px;
          font-weight: 400;
          margin: 0;
        }

        .header-greeting h1 {
          font-size: 14px;
          margin: 0;
          font-weight: 400;
          color: #c7d2fe;
        }

        .header-greeting strong {
          color: #ffffff;
          font-weight: 600;
        }

        .wave {
          display: inline-block;
          animation: wave-animation 2.5s infinite;
          transform-origin: 70% 70%;
          margin-left: 6px;
        }

        @keyframes wave-animation {
            0% { transform: rotate( 0.0deg) }
           10% { transform: rotate(14.0deg) }
           20% { transform: rotate(-8.0deg) }
           30% { transform: rotate(14.0deg) }
           40% { transform: rotate(-4.0deg) }
           50% { transform: rotate(10.0deg) }
           60% { transform: rotate( 0.0deg) }
          100% { transform: rotate( 0.0deg) }
        }

        .header-profile {
          display: flex;
          align-items: center;
          gap: 14px;
          cursor: pointer;
          padding: 8px 18px;
          border-radius: 12px;
          transition: all 0.2s ease;
          border: 1px solid rgba(255, 255, 255, 0.14);
          background: rgba(255, 255, 255, 0.08);
          font: inherit;
        }

        .header-profile:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.25);
        }

        .profile-info {
          display: flex;
          flex-direction: column;
          text-align: right;
          gap: 3px;
        }

        .profile-name {
          font-weight: 600;
          font-size: 0.92rem;
          color: #ffffff;
          line-height: 1.2;
        }

        .profile-role-badge {
          font-size: 0.65rem;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 5px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          display: inline-block;
          border: 1px solid;
          align-self: flex-end;
        }

        .profile-avatar {
          width: 34px;
          height: 34px;
          background: linear-gradient(135deg, #6366f1 0%, #4338ca 100%);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 0.95rem;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
          border: 2px solid rgba(255, 255, 255, 0.25);
        }

        @media (max-width: 768px) {
          .header {
            padding: 0 16px;
            height: 58px;
            min-height: 58px;
          }

          .header-greeting {
            display: none;
          }

          .header-active-badge {
            display: none;
          }

          .header-divider {
            display: none;
          }
        }
      `}</style>

      <header className="header">
        <div className="header-left">
          <div className="header-brand">
            <img src={logoImage} alt="Cognior" className="header-logo" />
            <span className="header-brand-title">COGNIOR</span>
          </div>

          <div className="header-divider" />

          <div className="header-active-badge">
            {activeLabel}
          </div>
        </div>

        <div className="header-right">
          <div className="header-greeting header-title">
            <h1>
              Welcome, <strong>{displayName}</strong>
              <span className="wave" role="img" aria-label="wave">👋</span>
            </h1>
          </div>

          <button
            type="button"
            className="header-profile"
            onClick={onProfileClick}
            aria-label="Open user profile"
          >
            <div className="profile-info">
              <span className="profile-name">{displayName}</span>
              <span
                className="profile-role-badge"
                style={{
                  backgroundColor: `${displayRole.color}25`,
                  color: '#ffffff',
                  borderColor: displayRole.color
                }}
              >
                {displayRole.label}
              </span>
            </div>
            <div className="profile-avatar">
              {username.charAt(0).toUpperCase()}
            </div>
          </button>
        </div>
      </header>
    </>
  );
};

export default Header;